/**
 * Assigned cM for MRCA paths from DNA Painter segments (overlap dedupe, DNA-flow style).
 * Avoid TypeScript tuple-array types like [number, number][] — they break CRA 3 / typescript-estree.
 */

export interface NormalizedDnaSegment {
  chromosome: number;
  start: number;
  end: number;
  cm: number;
  linked_individual_id: string;
}

export interface DnaSegmentBpCm {
  chromosome: number;
  start: number;
  end: number;
  cm: number;
}

export const APPROX_AUTOSOMAL_TOTAL_CM = 3587;

export function intervalUnionLength(intervals: number[][]): number {
  if (intervals.length === 0) return 0;
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
  let mergedEnd = sorted[0][1];
  let total = 0;
  let prevStart = sorted[0][0];
  for (let i = 1; i < sorted.length; i += 1) {
    const s = sorted[i][0];
    const e = sorted[i][1];
    if (s <= mergedEnd) {
      mergedEnd = Math.max(mergedEnd, e);
    } else {
      total += mergedEnd - prevStart;
      prevStart = s;
      mergedEnd = e;
    }
  }
  total += mergedEnd - prevStart;
  return total;
}

export function dedupeCmForLinkedSegments(segments: DnaSegmentBpCm[]): number {
  const byChrom: { [chrom: number]: number[][] } = {};
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i];
    const c = seg.chromosome;
    if (!byChrom[c]) byChrom[c] = [];
    byChrom[c].push([seg.start, seg.end, seg.cm]);
  }
  let totalCm = 0;
  const chromKeys = Object.keys(byChrom);
  for (let k = 0; k < chromKeys.length; k += 1) {
    const segs = byChrom[Number(chromKeys[k])];
    if (!segs || segs.length === 0) continue;
    let rawSumCm = 0;
    let totalBp = 0;
    for (let j = 0; j < segs.length; j += 1) {
      rawSumCm += segs[j][2];
      totalBp += segs[j][1] - segs[j][0];
    }
    if (totalBp <= 0) continue;
    const pairs: number[][] = [];
    for (let j = 0; j < segs.length; j += 1) {
      pairs.push([segs[j][0], segs[j][1]]);
    }
    const unionBp = intervalUnionLength(pairs);
    const scale = unionBp / totalBp;
    totalCm += rawSumCm * scale;
  }
  return Math.round(totalCm * 100) / 100;
}

/** Collapse whitespace, lowercase — for matching DNA Painter match names to Individual.dna_painter_match_name. */
export function normalizeMatchNameKey(name: string | undefined | null): string {
  if (name === undefined || name === null) return '';
  const t = String(name).trim();
  if (!t) return '';
  // Hyphens often differ between GEDCOM (e.g. MCNAMARA-SILK) and DNA Painter exports (space or hyphen).
  return t.toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ');
}

export function buildDnaPainterMatchNameToIndividualId(
  individuals: { id: string; dna_painter_match_name?: string | null }[]
): Record<string, string> {
  const map: Record<string, string> = {};
  for (let i = 0; i < individuals.length; i += 1) {
    const ind = individuals[i];
    const key = normalizeMatchNameKey(ind.dna_painter_match_name || '');
    if (key && !map[key]) map[key] = ind.id;
  }
  return map;
}

export function normalizePainterSegment(
  raw: Record<string, any>,
  matchNameToIndividualId?: Record<string, string>
): NormalizedDnaSegment | null {
  let linked: string | undefined =
    raw.linked_individual_id && typeof raw.linked_individual_id === 'string'
      ? raw.linked_individual_id
      : undefined;
  if (!linked && matchNameToIndividualId && raw.match_name) {
    const key = normalizeMatchNameKey(String(raw.match_name));
    if (key && matchNameToIndividualId[key]) linked = matchNameToIndividualId[key];
  }
  if (!linked || typeof linked !== 'string') return null;
  const chrom = Number(raw.chromosome);
  const start = Number(raw.start);
  const end = Number(raw.end);
  const cm = Number(raw.cm);
  if (!Number.isFinite(chrom) || chrom < 1 || chrom > 22) return null;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  if (!Number.isFinite(cm) || cm <= 0) return null;
  return { chromosome: chrom, start, end, cm, linked_individual_id: linked };
}

export type MrcaPathAttributionKind =
  | 'linked'
  | 'linked_profile'
  | 'suppressed_duplicate'
  | 'no_data'
  | 'unlinked';

export interface AssignedCmResult {
  byPath: Record<string, number>;
  warnings: string[];
  totalAssignedCm: number;
  pathKind: Record<string, MrcaPathAttributionKind>;
}

export interface WinningMrcaPathByIndividualResult {
  /** For each tree individual id, the single MRCA path used for attribution (most specific if duplicated). */
  winningPathByIndividualId: Record<string, string>;
  /** Human-readable warnings when one person is placed on multiple paths. */
  duplicatePathWarnings: string[];
}

/**
 * Same path-resolution rules as assigned cM: one tree person → one winning lineage path (longest / most specific).
 */
export function computeWinningPathByIndividualId(
  linkedIndividualIds: Record<string, string>
): WinningMrcaPathByIndividualResult {
  const duplicatePathWarnings: string[] = [];
  const idToPaths: { [id: string]: string[] } = {};

  const pathKeys = Object.keys(linkedIndividualIds);
  for (let i = 0; i < pathKeys.length; i += 1) {
    const path = pathKeys[i];
    const id = linkedIndividualIds[path];
    if (!id || !id.trim()) continue;
    if (!idToPaths[id]) idToPaths[id] = [];
    idToPaths[id].push(path);
  }

  const winningPathByIndividualId: Record<string, string> = {};
  const idKeys = Object.keys(idToPaths);
  for (let i = 0; i < idKeys.length; i += 1) {
    const id = idKeys[i];
    const paths = idToPaths[id];
    const seen: { [p: string]: boolean } = {};
    const unique: string[] = [];
    for (let j = 0; j < paths.length; j += 1) {
      if (!seen[paths[j]]) {
        seen[paths[j]] = true;
        unique.push(paths[j]);
      }
    }
    if (unique.length > 1) {
      unique.sort((a, b) => b.length - a.length || a.localeCompare(b));
      duplicatePathWarnings.push(
        'Same tree person on MRCA paths ' +
          unique.join(', ') +
          ' - DNA Painter cM is counted only on ' +
          unique[0] +
          ' (most specific).'
      );
    }
    const sorted = unique.slice().sort((a, b) => b.length - a.length || a.localeCompare(b));
    winningPathByIndividualId[id] = sorted[0];
  }

  return { winningPathByIndividualId, duplicatePathWarnings };
}

export interface ComputeAssignedCmOptions {
  /**
   * When deduped DNA Painter segments sum to 0, use this individual's shared_dna_cm (e.g. from FTDNA paste on profile).
   * Prefer segment totals when they are greater than 0 to avoid double-counting overlapping sources.
   */
  sharedCmFallbackByIndividualId?: Record<string, number>;
}

export function computeAssignedCmByMrcaPath(
  segments: NormalizedDnaSegment[],
  linkedIndividualIds: Record<string, string>,
  options?: ComputeAssignedCmOptions
): AssignedCmResult {
  const { winningPathByIndividualId, duplicatePathWarnings } =
    computeWinningPathByIndividualId(linkedIndividualIds);
  const warnings = duplicatePathWarnings.slice();

  const pathKeys = Object.keys(linkedIndividualIds);
  const fallback = options?.sharedCmFallbackByIndividualId || {};

  const byPath: Record<string, number> = {};
  const pathKind: Record<string, MrcaPathAttributionKind> = {};
  let totalAssignedCm = 0;

  for (let i = 0; i < pathKeys.length; i += 1) {
    const path = pathKeys[i];
    const id = linkedIndividualIds[path];
    if (!id || !id.trim()) {
      pathKind[path] = 'unlinked';
      byPath[path] = 0;
      continue;
    }
    if (winningPathByIndividualId[id] !== path) {
      byPath[path] = 0;
      pathKind[path] = 'suppressed_duplicate';
      continue;
    }
    const mine: NormalizedDnaSegment[] = [];
    for (let s = 0; s < segments.length; s += 1) {
      if (segments[s].linked_individual_id === id) mine.push(segments[s]);
    }
    const cmFromSegs = dedupeCmForLinkedSegments(mine);
    const fb = fallback[id];
    const fbNum = fb !== undefined && Number.isFinite(fb) && fb > 0 ? Math.round(fb * 100) / 100 : 0;
    let cm = cmFromSegs;
    let kind: MrcaPathAttributionKind;
    if (cmFromSegs > 0) {
      kind = 'linked';
    } else if (fbNum > 0) {
      cm = fbNum;
      kind = 'linked_profile';
    } else {
      kind = 'no_data';
    }
    byPath[path] = cm;
    pathKind[path] = kind;
    totalAssignedCm += cm;
  }

  totalAssignedCm = Math.round(totalAssignedCm * 100) / 100;

  return { byPath, warnings, totalAssignedCm, pathKind };
}

export function approximateGenomePercentFromCm(cm: number): number {
  if (cm <= 0 || !APPROX_AUTOSOMAL_TOTAL_CM) return 0;
  return Math.round((cm / APPROX_AUTOSOMAL_TOTAL_CM) * 10000) / 100;
}
